import { getMapById, reduceSumOn } from '@/services/sanitize.service';
import { fetchSubcontractors } from '@/features/bundles/bundles.service';

export function getTotalMarket(amendments) {
    return amendments.reduce(
        (acc, amendment) =>
            amendment.amountTTC && (amendment.type === 'market' || amendment.type === 'marketOption')
                ? acc + amendment.amountTTC
                : acc,
        0,
    );
}
export function getTotalAmendments(amendments) {
    const amendmentsType = [
        'tma',
        'choice',
        'conceptionError',
        'executionError',
        'unexpected',
        'marketUpdate',
        'other',
    ];
    return amendments.reduce(
        (acc, amendment) =>
            amendment.amountTTC && amendmentsType.includes(amendment.type) ? acc + amendment.amountTTC : acc,
        0,
    );
}
export function getTotalMarketPlusAmendments(amendments) {
    return getTotalMarket(amendments) + getTotalAmendments(amendments);
}

export function getTotalCaution(deductions) {
    return deductions.reduce(
        (acc, deduction) => (deduction.amountTTC && deduction.type === 'caution' ? acc + deduction.amountTTC : acc),
        0,
    );
}

export function getTotalDeductions(deductions) {
    return deductions.reduce(
        (acc, deduction) => (deduction.amountTTC && deduction.type === 'deduction' ? acc + deduction.amountTTC : acc),
        0,
    );
}
export function getTotalGaranteeDeductions(deductions) {
    return deductions.reduce(
        (acc, deduction) =>
            deduction.amountTTC && deduction.type === 'garanteeDeduction' ? acc + deduction.amountTTC : acc,
        0,
    );
}

export function getTotalPenalties(deductions) {
    return deductions.reduce(
        (acc, deduction) => (deduction.amountTTC && deduction.type === 'penalty' ? acc + deduction.amountTTC : acc),
        0,
    );
}
export function getTotalCie(cies) {
    return reduceSumOn(cies, 'amountTTC');
}

export function getTotalCieResponsability(cies, emitterId) {
    return cies.reduce(
        (acc, cie) =>
            acc +
            reduceSumOn(
                cie.distribution.filter((distribution) => !emitterId || distribution.bundleId === emitterId),
                'amount',
            ),
        0,
    );
}

export function getSituationMainCompany(invoices, mainCompanyBundleId) {
    return invoices.reduce((acc, invoice) => {
        const distribution = invoice.situationDistribution.find(
            (distribution) => distribution.id === mainCompanyBundleId,
        );
        if (distribution && invoice.situationEmissionDate) {
            return acc + distribution.amountTTC;
        } else {
            return acc;
        }
    }, 0);
}

export function getSituationSubcontractors(invoices, mainCompanyBundleId) {
    return invoices.reduce((acc, invoice) => {
        const distributions = invoice.situationDistribution.filter(
            (distribution) => distribution.id !== mainCompanyBundleId,
        );
        if (invoice.situationEmissionDate && distributions) {
            return acc + distributions.reduce((acc, line) => acc + line.amountTTC, 0);
        } else {
            return acc;
        }
    }, 0);
}
export function getSituationCIE(invoices) {
    return invoices.reduce((acc, invoice) => {
        const deductions = invoice.chargedDeductions.filter((deduction) => deduction.id === 'cie');
        if (invoice.situationEmissionDate && deductions) {
            return acc + deductions.reduce((acc, line) => acc + line.amountTTC, 0);
        } else {
            return acc;
        }
    }, 0);
}
export function getSituationGaranteeDeduction(invoices) {
    return invoices.reduce((acc, invoice) => {
        const deductions = invoice.chargedDeductions.filter((deduction) => deduction.id === 'garanteeDeduction');
        if (invoice.situationEmissionDate && deductions) {
            return acc + deductions.reduce((acc, line) => acc + line.amountTTC, 0);
        } else {
            return acc;
        }
    }, 0);
}
export function getSituationPenalties(invoices) {
    return invoices.reduce((acc, invoice) => {
        const deductions = invoice.chargedDeductions.filter((deduction) => deduction.id === 'penalty');
        if (invoice.situationEmissionDate && deductions) {
            return acc + deductions.reduce((acc, line) => acc + line.amountTTC, 0);
        } else {
            return acc;
        }
    }, 0);
}
export function getSituationOtherDeductions(invoices) {
    return invoices.reduce((acc, invoice) => {
        const deductions = invoice.chargedDeductions.filter(
            (deduction) => deduction.id !== 'penalty' && deduction.id !== 'garanteeDeduction' && deduction.id !== 'cie',
        );
        if (invoice.situationEmissionDate && deductions) {
            return acc + deductions.reduce((acc, line) => acc + line.amountTTC, 0);
        } else {
            return acc;
        }
    }, 0);
}

export async function buildSubcontractors(projectId, emitterId, distribution) {
    if (emitterId) {
        const distributionMap = getMapById(distribution);
        return (await fetchSubcontractors(projectId, emitterId)).map((subcontractor) => ({
            ...subcontractor,
            amountTTC: distributionMap[subcontractor.id]?.amountTTC || 0,
        }));
    } else {
        return [];
    }
}

export function getNewSituationDistribution(invoice, subcontractorDistribution) {
    const totalDeductions = reduceSumOn(invoice.chargedDeductions, 'amountTTC');
    const totalDistributionTTC = reduceSumOn(subcontractorDistribution, 'amountTTC');
    const emitterDistributionAmountTTC = invoice.amountTTC - totalDistributionTTC - totalDeductions;
    return [
        {
            id: invoice.emitterId,
            amountTTC: emitterDistributionAmountTTC,
        },
        ...subcontractorDistribution,
    ];
}
